import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux"; 
import {
  Form,
  Select,
  Input,
  Button,
  Card,
  Row,
  Col,
  Collapse,
  Table,
  Divider,
  message,
} from "antd";
import "./index.css";
import { getUserData } from "../../login/LoginSlice"; 
import {
  getPolicyNumber,
} from "../../../Action/Actions";
import { getCorporateName,getCorporateEndorsementRackRates,getEndorsementPolicyDetails,getEndorsementCalculation,createEndorsementCalculation } from "../../../Action/EndorsementcalcApi";
import SampleCsv from "../../sampleFile/endorsement_calculation_template.csv";

const { Option } = Select;

const renderNonZero = (text) => {
  if (text && parseFloat(text) !== 0) {
    if (typeof text === "number") {
      return text.toFixed(2);
    }
    return text;
  }
  return ""; 
};

const additionColumns = [
  { title: "Age Group", dataIndex: "ageGroup", key: "ageGroup" },
  { title: "Sum Insured", dataIndex: "sumInsured", key: "sumInsured" },
  {
    title: "Annual Premium",
    dataIndex: "annualPremium",
    key: "annualPremium",
  },
  {
    title: "Premium Per Day",
    dataIndex: "premiumPerDay",
    key: "premiumPerDay",
  },
  {
    title: "Total Days",
    dataIndex: "totalDays",
    key: "totalDays",
    render: renderNonZero,
  },
  {
    title: "Member Count",
    dataIndex: "memberCount",
    key: "memberCount",
    render: renderNonZero,
  },
  {
    title: "Addition Premium",
    dataIndex: "additionPremium",
    key: "additionPremium",
    render: renderNonZero,
  },
];
const deletionColumns = [
  ...additionColumns.slice(0, 6),
  {
    title: "Deletion Premium",
    dataIndex: "additionPremium",
    key: "additionPremium",
    render: renderNonZero,
  },
];
const generateRowKey = (record) => {
    const ageGroup = String(record.ageGroup || "").replace(/\s/g, "");
    const sumInsured = String(record.sumInsured || "");
    return `${ageGroup}-${sumInsured}`;
};


const EndorseCalc = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const AuthData = useSelector(getUserData);
  const [showPolicyDetails, setShowPolicyDetails] = useState(false);
  const [CorpName, setCorpName] = useState([]);
  const [PolicyNum, setPolicyNum] = useState([]);
  const [enrollmentType, setEnrollmentType] = useState(null);
  const [policyTypeId, setPolicyTypeId] = useState(null);
  const [enrollmentTypeId, setEnrollmentTypeId] = useState(null); 
  const [additionTableData, setAdditionTableData] = useState([]);
  const [deletionTableData, setDeletionTableData] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeFile, setEmployeeFile] = useState(null);
  const [endorsementUploadId, setEndorsementUploadId] = useState(null); 

  const resetFormState = () => {
    form.resetFields();
    setShowPolicyDetails(false);
    setPolicyNum([]);
    setEnrollmentType(null);
    setPolicyTypeId(null);
    setEnrollmentTypeId(null); 
    setAdditionTableData([]);
    setDeletionTableData([]);
    setEmployeeFile(null);
    setEndorsementUploadId(null); 
    setIsCalculating(false);
    setIsSubmitting(false);
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    document.title = `Endorsement Calculator`;
    dispatch(getCorporateName(AuthData?.corporate_id))
      .then((data) => setCorpName(data))
      .catch((error) => {
        console.error(error);
        message.error("Failed to fetch corporate names.");
      });
  }, [dispatch, AuthData?.corporate_id]);

  const GetPolicyNumber = (id) => {
    dispatch(getPolicyNumber(id))
      .then((data) => {
          const policies = data?.Policy_no || (Array.isArray(data) ? data : []);
          setPolicyNum(policies);
      })
      .catch((error) => {
        console.error(error);
        message.error("Failed to fetch policy numbers.");
        setPolicyNum([]);
      });
  };
  const OnNameSelect = (value, option) => {
    resetFormState();
    GetPolicyNumber(option.key);
    form.setFieldsValue({ corporate: value });
  };
  const OnPolicySelect = (value, option) => {
    const selectedPolicyId = option.key;
    setShowPolicyDetails(false);
    setAdditionTableData([]);
    setDeletionTableData([]);
    setEnrollmentType(null);
    setPolicyTypeId(null);
    setEnrollmentTypeId(null); 
    setEmployeeFile(null);
    setEndorsementUploadId(null); 
    form.resetFields([
      "total_addition_premium",
      "total_deletion_premium",
      "net_premium",
      "endorsement_title",
      "endorsement_number",
      "transaction_statement",
    ]);

    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }

    dispatch(getEndorsementPolicyDetails(selectedPolicyId))
      .then((policyDetails) => {
        form.setFieldsValue({
          policy_start_date: policyDetails.policy_start_date,
          policy_end_date: policyDetails.policy_end_date,
          policy_type: policyDetails.policy_type || "N/A",
          enrollment_type_dummy: policyDetails.enrollment_type || "N/A",
        });
        setEnrollmentType(policyDetails.enrollment_type);
        setPolicyTypeId(policyDetails.ref_md_policy_types_id);
        setEnrollmentTypeId(policyDetails.ref_enrollment_type_id); 
        setShowPolicyDetails(true);
      })
      .catch((error) => {
        console.error("Failed to fetch policy details:", error);
        message.error(String(error) || "Failed to fetch policy details.");
        setShowPolicyDetails(false);
      });

    dispatch(getCorporateEndorsementRackRates(selectedPolicyId))
      .then((rates) => {
        const baseTableData = rates.map((rate) => ({
          ...rate,
          ageGroup: String(rate.ageGroup || "").trim(),
          sumInsured: String(rate.sumInsured || "").replace(/[^\d.]/g, ""),
          annualPremium: String(rate.annualPremium || "").replace(/[^\d.]/g, ""),
          premiumPerDay: String(rate.premiumPerDay || "").replace(/[^\d.]/g, ""),
          memberCount: 0,
          totalDays: 0,
          additionPremium: 0,
        }));

        setAdditionTableData(baseTableData);
        setDeletionTableData(JSON.parse(JSON.stringify(baseTableData)));
        if (rates.length === 0) {
          message.info("No rack rates found for this policy.");
        }
      })
      .catch((error) => {
        console.error("Failed to fetch rack rates:", error);
        message.error(String(error) || "Could not fetch rack rates.");
        setAdditionTableData([]);
        setDeletionTableData([]);
      });
  };
  const updateTablesWithCalcData = (calcData) => {
    if (!calcData || (!calcData.addition && !calcData.deletion)) {
      message.error("Received invalid or incomplete calculation data from the server.");
      return;
    }
    const {
      addition,
      deletion,
      total_addition_premium,
      total_deletion_premium,
      net_premium,
    } = calcData;
    const createDataLookupMap = (apiSection) => {
      const lookupMap = new Map();
      if (!apiSection || !apiSection.sum_insured) return lookupMap;
      Object.entries(apiSection.sum_insured).forEach(
        ([sumInsured, ageGroupData]) => {
          Object.entries(ageGroupData).forEach(([ageGroup, values]) => {
            const key = generateRowKey({ ageGroup, sumInsured });
            lookupMap.set(key, values);
          });
        }
      );
      return lookupMap;
    };
    const additionMap = createDataLookupMap(addition);
    const deletionMap = createDataLookupMap(deletion);
    const mergeTableData = (baseTableData, calculatedDataMap) => {
      return baseTableData.map(row => {
          const key = generateRowKey(row);
          const calcResult = calculatedDataMap.get(key);
          if (calcResult) {
              return {
                  ...row,
                  memberCount: calcResult.member_count,
                  totalDays: calcResult.total_remaining_days,
                  additionPremium: calcResult.total_premium,
              };
          }
          return { ...row, memberCount: 0, totalDays: 0, additionPremium: 0 };
      });
    };
    setAdditionTableData(prevData => mergeTableData(prevData, additionMap));
    setDeletionTableData(prevData => mergeTableData(prevData, deletionMap));
    form.setFieldsValue({
      total_addition_premium: total_addition_premium?.toFixed(2) || '0.00',
      total_deletion_premium: total_deletion_premium?.toFixed(2) || '0.00',
      net_premium: net_premium?.toFixed(2) || '0.00',
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setEmployeeFile(file);
    setEndorsementUploadId(null); 
    const policyNumber = form.getFieldValue("policy_no");
    const selectedPolicy = PolicyNum.find(
      (p) => p.policy_number === policyNumber
    );
    if (!selectedPolicy?.id) {
      message.error("A policy must be selected before uploading a file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setEmployeeFile(null);
      return;
    }
    setIsCalculating(true);
    message.loading({ content: "Calculating...", key: "calculation_message" });

    dispatch(getEndorsementCalculation(selectedPolicy.id, file))
        .then((calcResponseData) => {
            message.success({ content: "Calculation Successful!", key: "calculation_message", duration: 2 });
            
            if (calcResponseData) {
                const dataToProcess = calcResponseData.data || calcResponseData;
                
                if (dataToProcess.ref_endorsment_calculation_data_upload_id) {
                    setEndorsementUploadId(dataToProcess.ref_endorsment_calculation_data_upload_id);
                    updateTablesWithCalcData(dataToProcess);
                } else {
                    message.error({ content: "Calculation error: Critical reference ID missing. Submission is disabled.", key: "calculation_message", duration: 5 });
                }
            } else {
                 message.error({ content: "Calculation error: Received an empty response from the server.", key: "calculation_message", duration: 5 });
            }
        })
        .catch((error) => {
            message.error({ content: String(error) || "Calculation failed. Please check the file format and try again.", key: "calculation_message", duration: 4 });
            setEmployeeFile(null); 
            setEndorsementUploadId(null);
        })
        .finally(() => {
            setIsCalculating(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        });
  };

  const handleSubmit = (values) => {
    const policyNumber = form.getFieldValue("policy_no");
    const selectedPolicy = PolicyNum.find((p) => p.policy_number === policyNumber);
    const corporateName = form.getFieldValue("corporate");
    const selectedCorporate = CorpName.find((c) => c.corporate_name === corporateName);

    if (!selectedPolicy?.id || !selectedCorporate?.corporate_id) {
      message.error("Corporate or policy selection is missing.");
      return;
    }

    if (!employeeFile) {
        message.error("Please upload and calculate an employee file before submitting.");
        return;
    }
    
    if (!endorsementUploadId) {
      message.error("Calculation reference ID is missing. Please ensure the employee file was calculated successfully before submitting.");
      return;
    }

    const allFormValues = form.getFieldsValue([
      "policy_start_date",
      "policy_end_date",
      "policy_type",
      "total_addition_premium",
      "total_deletion_premium",
      "net_premium",
      "endorsement_title",
      "endorsement_number",
      "transaction_statement",
    ]);

    const ref_policy_types_id = policyTypeId || 0;
    const ref_enrollment_type_id = enrollmentTypeId || 0;
    const enrollment_type_string = enrollmentType || "";

    const mergedData = [
      ...additionTableData
        .filter((row) => row.memberCount > 0)
        .map((row) => ({
          age_group: row.ageGroup,
          sum_insured: parseFloat(row.sumInsured),
          annual_premium: parseFloat(row.annualPremium),
          premium_per_day: parseFloat(row.premiumPerDay),
          total_days: row.totalDays,
          member_count: row.memberCount,
          premium_value: parseFloat(row.additionPremium),
          premium_type_id: 1,
          premium_type: "addition_premium",
        })),
      ...deletionTableData
        .filter((row) => row.memberCount > 0)
        .map((row) => ({
          age_group: row.ageGroup,
          sum_insured: parseFloat(row.sumInsured),
          annual_premium: parseFloat(row.annualPremium),
          premium_per_day: parseFloat(row.premiumPerDay),
          total_days: row.totalDays,
          member_count: row.memberCount,
          premium_value: parseFloat(row.additionPremium),
          premium_type_id: 2,
          premium_type: "deletion_premium",
        })),
    ];

    const payload = {
      ref_endorsment_calculation_data_upload_id: endorsementUploadId,
      ref_corporate_id: selectedCorporate.corporate_id,
      corporate_name: selectedCorporate.corporate_name,
      ref_policy_id: selectedPolicy.id,
      policy_number: selectedPolicy.policy_number,
      policy_start_date: allFormValues.policy_start_date,
      policy_end_date: allFormValues.policy_end_date,
      ref_policy_types_id,
      policy_type: allFormValues.policy_type,
      ref_enrollment_type_id,
      enrollment_type: enrollment_type_string,
      total_addition_premium: parseFloat(allFormValues.total_addition_premium),
      total_deletion_premium: parseFloat(allFormValues.total_deletion_premium),
      net_premium: parseFloat(allFormValues.net_premium),
      endorsement_title: allFormValues.endorsement_title,
      endorsement_number: allFormValues.endorsement_number,
      transaction_statements: allFormValues.transaction_statement,
      data: mergedData,
    };

    setIsSubmitting(true);
    message.loading({ content: "Submitting Endorsement...", key: "submit_msg" });

    dispatch(createEndorsementCalculation(payload))
      .then((response) => {
        message.success({
          content: response.message || "Endorsement created successfully!",
          key: "submit_msg",
          duration: 3,
        });
        resetFormState();
      })
      .catch((error) => {
        message.error({
          content: String(error) || "Failed to submit endorsement.",
          key: "submit_msg",
          duration: 5,
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleDownloadCSV = () => {
    const link = document.createElement("a");
    link.href = SampleCsv;
    link.download = "endorsement-calculation-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card>
      <div className="card-body">
        <div id="esclation-matrix-tab">
          <h4>Endorsement Calculator</h4>
          <Divider />
          <div className="mt-4">
            <Form
              id="endorsement-calc-form"
              layout="vertical"
              onFinish={handleSubmit}
              form={form}
            >
              <Row gutter={16}>
                <Col xs={24} sm={6}>
                  <Form.Item
                    name="corporate"
                    label="Select Corporate"
                    rules={[
                      {
                        required: true,
                        message: "Please select a corporate!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Corporate name"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onSelect={OnNameSelect}
                    >
                      {CorpName?.map((insurer) => (
                        <Option
                          key={insurer.corporate_id}
                          value={insurer.corporate_name}
                          label={insurer.corporate_name}
                        >
                          {insurer.corporate_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                  <Form.Item
                    name="policy_no"
                    label="Policy No"
                    rules={[
                      { required: true, message: "Please select a policy!" },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Policy"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onSelect={OnPolicySelect}
                    >
                      {PolicyNum?.map((insurer) => (
                        <Option
                          key={insurer.id}
                          value={insurer.policy_number}
                          label={insurer.policy_number}
                        >
                          {insurer.policy_number}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              {showPolicyDetails && (
                <>
                  <Row gutter={16}>
                    <Col xs={24} sm={6}>
                      <Form.Item
                        label="Policy Start Date"
                        name="policy_start_date"
                      >
                        <Input placeholder="" disabled />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Form.Item label="Policy End Date" name="policy_end_date">
                        <Input placeholder="" disabled />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Form.Item label="Policy Type" name="policy_type">
                        <Input placeholder="" disabled />
                      </Form.Item>
                    </Col>
                    {enrollmentType && (
                      <Col xs={24} sm={6}>
                        <Form.Item
                          label="Enrollment Type"
                          name="enrollment_type_dummy"
                        >
                          <Input placeholder="" disabled />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <hr />
                </>
              )}
              <Row>
                <Col span={24}>
                  <Card>
                    <div className="pb-0">
                      <Row style={{ alignItems: "center" }}>
                        <Col md={12}>
                          <h4 className="card-title">
                            Endorsement Calculation
                          </h4>
                        </Col>
                        <Col md={12}>
                          <div style={{ float: "right" }}>
                            <input
                              type="file"
                              className="file-input"
                              name="endorsement_file"
                              id="endorsement_file"
                              onChange={handleFileChange}
                              disabled={!showPolicyDetails || isCalculating || isSubmitting}
                              accept=".csv"
                              ref={fileInputRef}
                            />
                            <Button
                              type="link"
                              className="px-0"
                              onClick={handleDownloadCSV}
                            >
                              Download template
                            </Button>
                          </div>
                        </Col>
                      </Row>
                      <p
                        style={{ marginTop: -15 }}
                        className="text-muted card-sub-title mb-0"
                      >
                        The premium will get auto calculated, based on rack
                        rates you have created
                      </p>
                    </div>
                    <div className="card-body table-responsive">
                      <Collapse
                        className="custom-collapse"
                        style={{ marginBottom: "20px" }}
                        defaultActiveKey={["1"]}
                        items={[
                          {
                            key: "1",
                            label: "Addition Calculation",
                            children: (
                              <Table
                                columns={additionColumns}
                                dataSource={additionTableData}
                                pagination={false}
                                rowKey={(record) => `add-${generateRowKey(record)}`}
                              />
                            ),
                          },
                        ]}
                      />
                      <Collapse
                        className="custom-collapse-del"
                        defaultActiveKey={["2"]}
                        items={[
                          {
                            key: "2",
                            label: "Deletion Calculation",
                            children: (
                              <Table
                                columns={deletionColumns}
                                dataSource={deletionTableData}
                                pagination={false}
                                rowKey={(record) => `del-${generateRowKey(record)}`}
                              />
                            ),
                          },
                        ]}
                      />
                    </div>
                  </Card>
                </Col>
              </Row>
              <Row gutter={16} className="mt-3">
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Total Addition Premium"
                    name="total_addition_premium"
                  >
                    <Input placeholder="0.00" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Total Deletion Premium"
                    name="total_deletion_premium"
                  >
                    <Input placeholder="0.00" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Net Premium" name="net_premium">
                    <Input placeholder="0.00" disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Endorsement Title"
                    name="endorsement_title"
                    rules={[
                      { required: true, message: "Enter Endorsement Title" },
                    ]}
                  >
                    <Input placeholder="e.g., Q1 Additions" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Endorsement Number"
                    name="endorsement_number"
                    rules={[
                      { required: true, message: "Enter Endorsement Number" },
                    ]}
                  >
                    <Input placeholder="Enter unique number" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Transaction Statement"
                    name="transaction_statement"
                    rules={[
                      {
                        required: true,
                        message: "Enter Transaction Statement",
                      },
                    ]}
                  >
                    <Input placeholder="Enter statement details" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="btn btn-primary save-endorsement-calculation-button"
                  disabled={isCalculating || isSubmitting || !employeeFile || !endorsementUploadId}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default EndorseCalc;